import React, { useState, useEffect, useCallback } from 'react';
import { apiService } from '../services/api';
import './StaffRegistryView.css';

function StaffRegistryView({ isAdmin = false }) {
  const [staff, setStaff] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editingStaff, setEditingStaff] = useState(null);
  const [formData, setFormData] = useState({
    name: '',
    phone_number: '',
    telegram_user_id: ''
  });
  const [message, setMessage] = useState(null);
  const [deleteConfirm, setDeleteConfirm] = useState(null);

  const fetchStaff = useCallback(async () => {
    setLoading(true);
    try {
      const data = await apiService.getCaretakers();
      setStaff(data);
    } catch (error) {
      console.error('Error fetching staff:', error);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchStaff();
  }, [fetchStaff]);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleAdd = () => {
    setEditingStaff(null);
    setFormData({ name: '', phone_number: '', telegram_user_id: '' });
    setShowModal(true);
  };

  const handleEdit = (member) => {
    setEditingStaff(member);
    setFormData({
      name: member.name,
      phone_number: member.phone_number,
      telegram_user_id: member.telegram_user_id || ''
    });
    setShowModal(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setMessage(null);

    try {
      if (editingStaff) {
        // Update existing staff
        await apiService.updateCaretaker(editingStaff.id, {
          ...formData,
          registered_by: 'Admin'
        });
        setMessage({ type: 'success', text: 'Support staff member updated successfully!' });
      } else {
        // Create new staff
        await apiService.createCaretaker({
          ...formData,
          registered_by: 'Admin'
        });
        setMessage({ type: 'success', text: 'Support staff member added successfully!' });
      }

      setFormData({ name: '', phone_number: '', telegram_user_id: '' });
      setShowModal(false);
      setEditingStaff(null);
      fetchStaff();
    } catch (error) {
      setMessage({ type: 'error', text: error.message || 'Failed to save staff member' });
    }
  };

  const handleDelete = async (member) => {
    try {
      await apiService.deleteCaretaker(member.id);
      setMessage({ type: 'success', text: 'Support staff member deleted successfully!' });
      setDeleteConfirm(null);
      fetchStaff();
    } catch (error) {
      const errorMsg = error.message || 'Failed to delete staff member';
      setMessage({ 
        type: 'error', 
        text: errorMsg.includes('Cannot delete') ? errorMsg : 'Failed to delete staff member'
      });
      setDeleteConfirm(null);
    }
  };

  return (
    <div className="staff-registry-view">
      <div className="view-header">
        <div>
          <h1 className="view-title">Support Staff Registry</h1>
          <p className="view-subtitle">Manage support staff members and their contact information</p>
        </div>
        {isAdmin && (
          <button className="add-staff-btn" onClick={handleAdd}>
            <span>+</span> Add Staff Member
          </button>
        )}
      </div>

      {message && (
        <div className={`message ${message.type}`}>
          {message.type === 'success' ? '✓' : '⚠️'} {message.text}
        </div>
      )}

      {loading ? (
        <div className="loading-state">
          <div className="loading-spinner"></div>
          <p>Loading staff members...</p>
        </div>
      ) : staff.length === 0 ? (
        <div className="empty-state">
          <div className="empty-icon">👥</div>
          <h3>No staff members registered</h3>
          <p>{isAdmin ? 'Add your first support staff member to get started' : 'No staff members registered yet'}</p>
          {isAdmin && (
            <button className="add-staff-btn" onClick={handleAdd}>
              <span>+</span> Add Staff Member
            </button>
          )}
        </div>
      ) : (
        <div className="staff-grid">
          {staff.map((member) => (
            <div key={member.id} className="staff-card">
              <div className="staff-avatar">
                {member.name.charAt(0).toUpperCase()}
              </div>
              <div className="staff-info">
                <h3 className="staff-name">{member.name}</h3>
                <div className="staff-detail">
                  <span className="detail-icon">📱</span>
                  <span>{member.phone_number}</span>
                </div>
                {member.telegram_user_id && (
                  <div className="staff-detail">
                    <span className="detail-icon">💬</span>
                    <span>Telegram: {member.telegram_user_id}</span>
                  </div>
                )}
                <div className="staff-meta">
                  <small>Registered by {member.registered_by}</small>
                  <small>{new Date(member.created_at).toLocaleDateString()}</small>
                </div>
              </div>
              {isAdmin && (
                <div className="staff-actions">
                  <button 
                    className="staff-action-btn edit-btn"
                    onClick={() => handleEdit(member)}
                    title="Edit staff member"
                  >
                    ✏️
                  </button>
                  <button 
                    className="staff-action-btn delete-btn"
                    onClick={() => setDeleteConfirm(member)}
                    title="Delete staff member"
                  >
                    🗑️
                  </button>
                </div>
              )}
            </div>
          ))}
        </div>
      )}

      {/* Add/Edit Staff Modal */}
      {showModal && (
        <div className="modal-overlay" onClick={() => setShowModal(false)}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2>{editingStaff ? 'Edit' : 'Add'} Support Staff Member</h2>
              <button className="modal-close" onClick={() => setShowModal(false)}>
                ✕
              </button>
            </div>

            <form onSubmit={handleSubmit} className="staff-form">
              <div className="form-group">
                <label htmlFor="name">Full Name *</label>
                <input
                  type="text"
                  id="name"
                  name="name"
                  value={formData.name}
                  onChange={handleInputChange}
                  required
                  placeholder="Enter full name"
                />
              </div>

              <div className="form-group">
                <label htmlFor="phone_number">Phone Number *</label>
                <input
                  type="tel"
                  id="phone_number"
                  name="phone_number"
                  value={formData.phone_number}
                  onChange={handleInputChange}
                  required
                  placeholder="+1-306-555-0123"
                />
              </div>

              <div className="form-group">
                <label htmlFor="telegram_user_id">Telegram ID (Optional)</label>
                <input
                  type="text"
                  id="telegram_user_id"
                  name="telegram_user_id"
                  value={formData.telegram_user_id}
                  onChange={handleInputChange}
                  placeholder="Telegram user ID"
                />
                <small className="form-hint">
                  Telegram ID allows staff to submit receipts via Telegram bot
                </small>
              </div>

              {message && message.type === 'error' && (
                <div className="form-message error">
                  ⚠️ {message.text}
                </div>
              )}

              <div className="form-actions">
                <button type="button" className="btn-secondary" onClick={() => setShowModal(false)}>
                  Cancel
                </button>
                <button type="submit" className="btn-primary">
                  <span>{editingStaff ? '✓' : '+'}</span> {editingStaff ? 'Update' : 'Add'} Staff Member
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Delete Confirmation Modal */}
      {deleteConfirm && (
        <div className="modal-overlay" onClick={() => setDeleteConfirm(null)}>
          <div className="modal-content delete-modal" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2>Confirm Deletion</h2>
              <button className="modal-close" onClick={() => setDeleteConfirm(null)}>
                ✕
              </button>
            </div>

            <div className="modal-body">
              <div className="warning-icon">⚠️</div>
              <p>Are you sure you want to delete <strong>{deleteConfirm.name}</strong>?</p>
              <p className="warning-text">
                This staff member must not have any associated receipts to be deleted.
              </p>
            </div>

            <div className="form-actions">
              <button className="btn-secondary" onClick={() => setDeleteConfirm(null)}>
                Cancel
              </button>
              <button className="btn-danger" onClick={() => handleDelete(deleteConfirm)}>
                <span>🗑️</span> Delete Staff Member
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default StaffRegistryView;
